import { useEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { Box, Portal } from '@chakra-ui/react';
import { useExitTransition } from '@/hooks/useExitTransition';
import { SHEET_EXIT_MS, sheetAnimation, sheetClassName } from '@/utils/sheetMotion';
import { DRAG_START_PX, dragOffset, shouldDismissSheet } from './sheetDrag';

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  /** id of the element that names the sheet. */
  labelledBy: string;
  children: ReactNode;
  /** Pass-through data hooks other code relies on (e.g. outside-click guards). */
  [dataAttribute: `data-${string}`]: string | boolean | undefined;
};

type Drag = {
  pointerId: number;
  startY: number;
  height: number;
  active: boolean;
  lastY: number;
  lastT: number;
  prevY: number;
  prevT: number;
};

/**
 * Mobile bottom sheet: slides up over a scrim, closes on scrim tap or Escape, and can be
 * dragged down to dismiss. Dragging starts only inside an element marked
 * `data-sheet-drag-handle` (the grabber and header), so the content keeps its own
 * scrolling and row swipes.
 */
export function BottomSheet({ open, onClose, labelledBy, children, ...dataAttributes }: BottomSheetProps) {
  const { mounted, closing } = useExitTransition(open, SHEET_EXIT_MS);
  const drag = useRef<Drag | null>(null);
  // A drag that ends over a button would otherwise click it.
  const swallowClick = useRef(false);
  const [dragY, setDragY] = useState<number | null>(null);
  const [dragHeight, setDragHeight] = useState(1);
  // Entrance finished: from here the sheet rests with no animation, so ending a drag does
  // not replay the slide-in.
  const [entered, setEntered] = useState(false);
  // Dismissed by a drag: it leaves from where the finger let go, not from the top.
  const [flung, setFlung] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEntered(false);
    setFlung(false);
    setDragY(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    swallowClick.current = false;
    if (closing || !(e.target as Element).closest('[data-sheet-drag-handle]')) return;
    drag.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      // jsdom has no layout; a real sheet always has a height.
      height: e.currentTarget.offsetHeight || window.innerHeight,
      active: false,
      lastY: e.clientY,
      lastT: e.timeStamp,
      prevY: e.clientY,
      prevT: e.timeStamp,
    };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dy = e.clientY - d.startY;
    if (!d.active) {
      if (Math.abs(dy) < DRAG_START_PX) return;
      d.active = true;
      setDragHeight(d.height);
      try {
        // Keep receiving moves once the finger leaves the handle.
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {
        // Throws if the pointer is already gone; the drag still works without capture.
      }
    }
    d.prevY = d.lastY;
    d.prevT = d.lastT;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    setDragY(dy);
  };

  const handlePointerEnd = (e: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (!d.active) return;
    swallowClick.current = true;
    const dt = d.lastT - d.prevT;
    const velocity = dt > 0 ? (d.lastY - d.prevY) / dt : 0;
    setDragY(null);
    if (!cancelled && shouldDismissSheet(d.lastY - d.startY, velocity, d.height)) {
      setFlung(true);
      onClose();
    }
  };

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!swallowClick.current) return;
    swallowClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  const dragging = dragY !== null;
  const progress = dragging ? Math.min(1, Math.max(0, dragY) / dragHeight) : 0;

  let transform: string | undefined;
  if (flung) transform = 'translateY(100%)';
  else if (dragging) transform = `translateY(${dragOffset(dragY)}px)`;

  let transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';
  if (dragging) transition = 'none';
  else if (flung) transition = `transform ${SHEET_EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1)`;

  let animation = 'none';
  if (closing && !flung) animation = sheetAnimation(true);
  else if (!closing && !entered) animation = sheetAnimation(false);

  return (
    <Portal>
      <Box position="fixed" inset={0} zIndex="modal" pointerEvents={closing ? 'none' : 'auto'}>
        <Box
          position="absolute"
          inset={0}
          bg="rgba(0, 0, 0, 0.35)"
          animation="fadeIn 240ms ease-out"
          // Per-frame values go inline: as style props they would mint a class per move.
          style={{
            opacity: closing ? 0 : 1 - progress,
            transition: dragging ? 'none' : `opacity ${SHEET_EXIT_MS}ms ease-in`,
          }}
          onClick={onClose}
        />
        <Box
          {...dataAttributes}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-hidden={closing || undefined}
          className={sheetClassName(closing)}
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          maxH="85dvh"
          display="flex"
          flexDirection="column"
          bg="white"
          borderTopRadius="20px"
          boxShadow="0 -4px 24px rgba(31, 41, 50, 0.16)"
          animation={animation}
          style={{ transform, transition }}
          onAnimationEnd={() => {
            if (!closing) setEntered(true);
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={(e: PointerEvent<HTMLDivElement>) => handlePointerEnd(e, false)}
          onPointerCancel={(e: PointerEvent<HTMLDivElement>) => handlePointerEnd(e, true)}
          onClickCapture={handleClickCapture}
        >
          {children}
        </Box>
      </Box>
    </Portal>
  );
}
